/**
 * Reusable time-series chart with axes, dots, hover tooltip, and a range
 * pill bar (W / M / 3M / Y / All). The chart's own viewBox stays at a fixed
 * pixel size; the browser scales the SVG to the container width.
 *
 * Used by:
 *   • HealthMeasurements — single-metric drill-down chart
 *   • HealthSleep        — duration + score charts
 *
 * The component owns the range state internally so consumers don't have to
 * thread it. Two `TimeSeriesChart`s on the same page therefore pick their
 * own ranges — that's fine, the cost is a few clicks if the user wants
 * them in sync, and it keeps the component self-contained.
 */

import { useMemo, useRef, useState } from 'react';

export interface ChartPoint {
  /** Epoch ms. */
  t: number;
  v: number;
}

export type TimeRange = 'W' | 'M' | '3M' | 'Y' | 'All';

const RANGE_OPTIONS: Array<{ id: TimeRange; label: string; days: number | null }> = [
  { id: 'W',   label: 'W',   days: 7 },
  { id: 'M',   label: 'M',   days: 30 },
  { id: '3M',  label: '3M',  days: 90 },
  { id: 'Y',   label: 'Y',   days: 365 },
  { id: 'All', label: 'All', days: null },
];

export interface TimeSeriesChartProps {
  /** Display label, e.g. "Weight" or "Sleep duration". */
  label: string;
  /** Unit suffix on values, e.g. "kg" or "h". Pass null to omit. */
  unit: string | null;
  /** Points sorted ascending by `t`. Empty array → empty state. */
  points: ChartPoint[];
  /** Default selected range. */
  defaultRange?: TimeRange;
  /** Optional right-hand subtitle (e.g. point count). */
  subtitle?: string | undefined;
  /** Override the line / dot color. */
  color?: string | undefined;
  /** Height of the chart in viewBox units. Defaults to 240. */
  height?: number | undefined;
}

export function TimeSeriesChart({
  label,
  unit,
  points: allPoints,
  defaultRange = 'M',
  subtitle,
  color = 'var(--accent)',
  height = 240,
}: TimeSeriesChartProps) {
  const [range, setRange] = useState<TimeRange>(defaultRange);

  // Filter to the selected window. The cutoff is computed from "now" — when
  // there is no data inside the window, fall back to "All" silently so the
  // chart doesn't render an empty rectangle.
  const points = useMemo(() => {
    const cutoff = cutoffMs(range);
    if (cutoff == null) return allPoints;
    const filtered = allPoints.filter((p) => p.t >= cutoff);
    return filtered.length > 0 ? filtered : allPoints;
  }, [allPoints, range]);

  return (
    <div
      style={{
        border: '1px solid var(--line)',
        borderRadius: 8,
        background: 'var(--canvas)',
        padding: 12,
        position: 'relative',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 10,
          flexWrap: 'wrap',
        }}
      >
        <strong style={{ fontSize: 14 }}>{label}</strong>
        {subtitle && (
          <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>{subtitle}</span>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          {RANGE_OPTIONS.map((r) => (
            <button
              key={r.id}
              type="button"
              className={`kos-filter-chip ${range === r.id ? 'kos-filter-chip-active' : ''}`}
              onClick={() => setRange(r.id)}
              title={
                r.days == null
                  ? 'All time'
                  : `Last ${r.days < 31 ? `${r.days} days` : r.days < 100 ? '3 months' : 'year'}`
              }
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
      {points.length === 0 ? (
        <div
          style={{
            padding: '32px 12px',
            fontSize: 13,
            color: 'var(--ink-3)',
            textAlign: 'center',
          }}
        >
          No data in this window.
        </div>
      ) : (
        <ChartBody points={points} unit={unit} label={label} color={color} height={height} />
      )}
    </div>
  );
}

function cutoffMs(range: TimeRange): number | null {
  const map: Record<TimeRange, number | null> = {
    W: 7,
    M: 30,
    '3M': 90,
    Y: 365,
    All: null,
  };
  const days = map[range];
  if (days == null) return null;
  return Date.now() - days * 86_400_000;
}

/**
 * The actual SVG body. Split out from the wrapper so the wrapper can render
 * a friendly empty state without setting up scales / hooks for points it
 * doesn't have.
 */
function ChartBody({
  points,
  unit,
  label,
  color,
  height,
}: {
  points: ChartPoint[];
  unit: string | null;
  label: string;
  color: string;
  height: number;
}) {
  const W = 800;
  const H = height;
  const padL = 44;
  const padR = 20;
  const padT = 16;
  const padB = 28;

  const xs = points.map((p) => p.t);
  const ys = points.map((p) => p.v);
  const minT = Math.min(...xs);
  const maxT = Math.max(...xs);
  const minV = Math.min(...ys);
  const maxV = Math.max(...ys);
  const padY = (maxV - minV) * 0.15 || 1;
  const yLo = minV - padY;
  const yHi = maxV + padY;
  const xScale = (t: number): number =>
    padL +
    (maxT === minT
      ? (W - padL - padR) / 2
      : ((t - minT) / (maxT - minT)) * (W - padL - padR));
  const yScale = (v: number): number =>
    padT + (H - padT - padB) * (1 - (v - yLo) / (yHi - yLo));

  const linePts = points
    .map((p) => `${xScale(p.t).toFixed(2)},${yScale(p.v).toFixed(2)}`)
    .join(' ');

  const yTicks = [yLo, yLo + (yHi - yLo) * 0.33, yLo + (yHi - yLo) * 0.66, yHi];
  const nXTicks = Math.min(6, points.length);
  const xTicks: number[] = [];
  if (nXTicks === 1) {
    xTicks.push(minT);
  } else {
    for (let i = 0; i < nXTicks; i++) {
      xTicks.push(minT + ((maxT - minT) * i) / (nXTicks - 1));
    }
  }

  const containerRef = useRef<HTMLDivElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const hoverPx = useMemo(() => {
    if (hoverIdx == null) return null;
    const p = points[hoverIdx];
    if (!p) return null;
    const svg = svgRef.current;
    const container = containerRef.current;
    if (!svg || !container) return null;
    const svgRect = svg.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const scaleX = svgRect.width / W;
    const scaleY = svgRect.height / H;
    return {
      x: svgRect.left - containerRect.left + xScale(p.t) * scaleX,
      y: svgRect.top - containerRect.top + yScale(p.v) * scaleY,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoverIdx]);

  const hovered = hoverIdx != null ? points[hoverIdx] : null;

  const latest = points[points.length - 1]!.v;
  const min = ys.reduce((a, b) => Math.min(a, b), Infinity);
  const max = ys.reduce((a, b) => Math.max(a, b), -Infinity);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <div
        style={{
          fontSize: 13,
          color: 'var(--ink-3)',
          marginBottom: 4,
          display: 'flex',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <span>
          {points.length} {points.length === 1 ? 'point' : 'points'}
        </span>
        <span>
          latest{' '}
          <strong style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink)' }}>
            {formatValue(latest)}
          </strong>
          {unit ? ` ${unit}` : ''}
        </span>
        <span>
          range{' '}
          <span style={{ fontFamily: 'var(--font-mono)' }}>
            {formatValue(min)}–{formatValue(max)}
          </span>
        </span>
      </div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height="auto"
        style={{ display: 'block' }}
        onMouseLeave={() => setHoverIdx(null)}
      >
        {yTicks.map((v, i) => {
          const y = yScale(v);
          return (
            <g key={i}>
              <line
                x1={padL}
                x2={W - padR}
                y1={y}
                y2={y}
                stroke="var(--line-2, var(--line))"
                strokeDasharray={i === 0 || i === yTicks.length - 1 ? '0' : '2 4'}
              />
              <text
                x={padL - 6}
                y={y + 3}
                textAnchor="end"
                fontSize="10"
                fill="var(--ink-3)"
                fontFamily="var(--font-mono)"
              >
                {formatValue(v)}
              </text>
            </g>
          );
        })}
        {xTicks.map((t, i) => {
          const x = xScale(t);
          return (
            <text
              key={i}
              x={x}
              y={H - 8}
              textAnchor="middle"
              fontSize="10"
              fill="var(--ink-3)"
            >
              {formatTick(t, maxT - minT)}
            </text>
          );
        })}

        <polyline points={linePts} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" />

        {hovered && (
          <line
            x1={xScale(hovered.t)}
            x2={xScale(hovered.t)}
            y1={padT}
            y2={H - padB}
            stroke={color}
            strokeWidth={1}
            strokeDasharray="2 3"
            opacity={0.4}
            pointerEvents="none"
          />
        )}

        {points.map((p, i) => (
          <g key={i}>
            <circle
              cx={xScale(p.t)}
              cy={yScale(p.v)}
              r={hoverIdx === i ? 5 : 3}
              fill={hoverIdx === i ? color : 'var(--canvas)'}
              stroke={color}
              strokeWidth={1.5}
              pointerEvents="none"
            />
            <circle
              cx={xScale(p.t)}
              cy={yScale(p.v)}
              r={14}
              fill="transparent"
              onMouseEnter={() => setHoverIdx(i)}
              style={{ cursor: 'crosshair' }}
            />
          </g>
        ))}
      </svg>

      {hovered && hoverPx && (
        <ChartTooltip
          point={hovered}
          unit={unit}
          label={label}
          x={hoverPx.x}
          y={hoverPx.y}
          containerWidth={containerRef.current?.clientWidth ?? W}
        />
      )}
    </div>
  );
}

function ChartTooltip({
  point,
  unit,
  label,
  x,
  y,
  containerWidth,
}: {
  point: ChartPoint;
  unit: string | null;
  label: string;
  x: number;
  y: number;
  containerWidth: number;
}) {
  const TOOLTIP_W = 180;
  const flip = x + TOOLTIP_W + 20 > containerWidth;
  const left = flip ? x - TOOLTIP_W - 12 : x + 12;
  const top = y - 30;
  const d = new Date(point.t);
  return (
    <div
      style={{
        position: 'absolute',
        left,
        top,
        width: TOOLTIP_W,
        pointerEvents: 'none',
        background: 'var(--canvas)',
        border: '1px solid var(--line)',
        borderRadius: 6,
        boxShadow: '0 8px 20px -6px color-mix(in oklab, #000 20%, transparent)',
        padding: '8px 10px',
        fontSize: 13,
        zIndex: 5,
      }}
    >
      <div
        style={{
          color: 'var(--ink-3)',
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '50px 1fr', rowGap: 2 }}>
        <span style={{ color: 'var(--ink-3)' }}>Date</span>
        <span style={{ fontFamily: 'var(--font-mono)' }}>
          {d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })}
        </span>
        <span style={{ color: 'var(--ink-3)' }}>Time</span>
        <span style={{ fontFamily: 'var(--font-mono)' }}>
          {d.toLocaleTimeString(undefined, {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          })}
        </span>
        <span style={{ color: 'var(--ink-3)' }}>Value</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
          {formatValue(point.v)}
          {unit ? ` ${unit}` : ''}
        </span>
      </div>
    </div>
  );
}

function formatValue(v: number): string {
  if (Number.isInteger(v)) return String(v);
  const abs = Math.abs(v);
  return abs >= 100 ? v.toFixed(1) : v.toFixed(2);
}

function formatTick(t: number, rangeMs: number): string {
  const d = new Date(t);
  if (rangeMs < 2 * 86_400_000) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}
